/**
 * Unsaved changes (AdminScreenTypes §3.6). A screen tells the guard how to read the state
 * the user edits; `markClean()` takes a snapshot of it — after the screen's data loads and
 * after every save — and the screen is dirty while the current state differs from that
 * snapshot. Until the first `markClean()` nothing is dirty (a loading screen never asks).
 *
 * The snapshot is a JSON string, never a reference: editors keep the model's own items
 * array in their form, so comparing the form with the model would always look clean.
 * `read` must therefore return plain data (models through `$toJson(false)` — attributes,
 * not the loaded relations).
 *
 * One router guard asks before leaving a dirty screen and the browser asks on reload or
 * tab close (both in router/guards.ts, through `anyDirty()`). A screen marks itself clean
 * before its own navigation after a save, record or delete. An item editor registers its
 * line the same way, counted only while its dialog is open (`active`).
 *
 * While a screen is saving the changes it holds, the guard refuses to leave without asking
 * (`anySaving()`): the save may still succeed, so "leave without saving?" would be wrong
 * either way. Its own navigation after the save follows `markClean()`, so it passes.
 * `createSaveLock()` gives a document editor that saving flag.
 */
import { computed, onBeforeUnmount, ref, shallowRef, type ComputedRef, type Ref, type ShallowRef } from 'vue'

interface DirtySource {
    isDirty: () => boolean
    isSaving: () => boolean
}

const sources = new Set<DirtySource>()

/**
 * Set once the user agreed to leave with the changes unsaved (`releaseChanges()` — logout or
 * a jump to the POS asks before it runs), so the route guard and the browser do not ask again
 * on the way out. The next completed navigation clears it (router/guards.ts).
 */
let released = false

export function releaseChanges(): void {
    released = true
}

export function clearRelease(): void {
    released = false
}

/**
 * A number as a string: up to 15 digits (a longer digit string — an e-invoice id — would lose
 * digits as a number) and no leading zero, so identifiers such as a PIB "02345678" stay text
 * and a fix of their leading zero is a change.
 */
const NUMERIC = /^-?(0|[1-9]\d{0,14})(\.\d+)?$/

/**
 * Values that read the same compare equal: a number written back in another form ('0.0000'
 * as '0'), and a cleared text field leaves '' where the form held null.
 * The cost: in a text that reads as a decimal, trailing zeros alone ("123.10" → "123.1") go
 * unseen.
 */
function normalize(_key: string, value: unknown): unknown {
    if (value === '') return null
    if (typeof value === 'string' && NUMERIC.test(value)) return Number(value)
    return value
}

/** True while any mounted screen holds changes it has not saved. */
export function anyDirty(): boolean {
    if (released) return false
    for (const source of sources) {
        if (source.isDirty()) return true
    }
    return false
}

/** True while a mounted screen is saving changes it still holds. */
export function anySaving(): boolean {
    if (released) return false
    for (const source of sources) {
        if (source.isSaving() && source.isDirty()) return true
    }
    return false
}

export interface DirtyGuardOptions {
    /** A save (or send) of the screen's changes is in flight. */
    saving?: () => boolean
    /**
     * The state counts only while this holds: an item editor's line counts while its dialog is
     * open (the page's snapshot does not hold it yet). Closed, it never does — Poništi / ✕ drop
     * the line on purpose, Sačuvaj handed it to the page.
     */
    active?: () => boolean
}

export interface DirtyGuard {
    /** The state differs from the last snapshot. */
    dirty: ComputedRef<boolean>
    /** Takes the snapshot: the current state counts as saved. */
    markClean: () => void
}

/**
 * The registration half, without the component lifecycle — `useDirtyGuard` ties it to the
 * calling component; specs call it directly.
 */
export function createDirtyGuard(read: () => unknown, options: DirtyGuardOptions = {}): DirtyGuard & { dispose: () => void } {
    const snapshot = ref<string | null>(null)
    const serialize = () => JSON.stringify(read(), normalize)

    const dirty = computed(() => (options.active?.() ?? true)
        && snapshot.value !== null
        && serialize() !== snapshot.value)
    const source: DirtySource = {
        isDirty: () => dirty.value,
        isSaving: () => options.saving?.() ?? false
    }
    sources.add(source)

    return {
        dirty,
        markClean: () => { snapshot.value = serialize() },
        dispose: () => { sources.delete(source) }
    }
}

export interface SaveLock<A extends string> {
    /** The action whose request is in flight — the button that shows the spinner. */
    pending: Readonly<Ref<A | null>>
    /** A request is in flight: every action waits (and the leave guard's `saving` reads it). */
    saving: ComputedRef<boolean>
    /** Runs `task` unless one is already in flight — then nothing is sent (resolves undefined). */
    run: <T>(action: A, task: () => Promise<T>) => Promise<T | undefined>
}

/**
 * The requests that change a document (save, record, rollback, delete, import) go out one at a
 * time: a double click on Sačuvaj sends one draft, and the flag holds until the whole task
 * settles — Proknjiži's save and its record request count as one, so a click between them
 * cannot save and record the document twice (the server's "already recorded" check is no
 * lock). The check and the claim happen in the same tick as the click.
 */
export function createSaveLock<A extends string = string>(): SaveLock<A> {
    // shallowRef's return type is conditional on its argument, which TS cannot resolve for the
    // generic A — the assertion names the type it is at runtime.
    const pending = shallowRef(null) as ShallowRef<A | null>

    async function run<T>(action: A, task: () => Promise<T>): Promise<T | undefined> {
        if (pending.value !== null) return undefined
        pending.value = action
        try {
            return await task()
        } finally {
            pending.value = null
        }
    }

    return { pending, saving: computed(() => pending.value !== null), run }
}

export function useDirtyGuard(read: () => unknown, options: DirtyGuardOptions = {}): DirtyGuard & {
    /** False once the screen is gone: a response that lands later must not navigate away
     *  from wherever the user went meanwhile. */
    onScreen: () => boolean
} {
    const { dirty, markClean, dispose } = createDirtyGuard(read, options)
    let mounted = true
    // The leave guard runs before the screen unmounts, so a confirmed leave still sees it.
    onBeforeUnmount(() => {
        mounted = false
        dispose()
    })
    return { dirty, markClean, onScreen: () => mounted }
}
